/**
 * Fetches the cached structural outline of a case's uploaded report
 * template. Backed by `GET /api/v1/cases/get-case-by-case-number/
 * {case_number}/template-outline`. The backend extracts and caches the
 * outline lazily on the first request (`services/template_outline.py`),
 * so the first hit pays one .docx parse + persist; subsequent hits
 * serve from the `cases.report_template_outline` JSON column.
 *
 * The outline drives the Report Builder's heading detection — see
 * `pages/ReportStudio/ReportBuilder/editor/qta-insert.ts:isHeadingPara`
 * for how it's consumed alongside the local heuristic fallback.
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";

export type TemplateOutlineEntryType =
  | "heading"
  | "body"
  | "signature"
  | "closing"
  | "chrome";

export type TemplateOutlineEntry = {
  index: number;
  text: string;
  type: TemplateOutlineEntryType;
  /** OOXML w14:paraId fingerprint when present in the source .docx. */
  paraId?: string;
  /** Heading level 1-9 when `type === "heading"`. */
  level?: number;
  /** Word style id, e.g. `Heading2`. */
  styleId?: string;
};

export type TemplateOutlineAnchor = {
  index: number;
  text: string;
};

export type TemplateOutline = {
  /** `cases.report_template_path` the outline was computed from. */
  sourcePath: string;
  paragraphs: TemplateOutlineEntry[];
  version: number;
  /** Auto-discovered CoC insertion anchor — the first **non-preamble**
   *  heading after the case-context opening (`Thank you for your referral…`).
   *  Headings the app owns and inserts itself (Code of Conduct, Conflict of
   *  Interest, Attendees, Unusual Circumstances) are skipped, so the inserts
   *  land after a template-owned Code of Conduct rather than ahead of it
   *  (MDRS-509). `null` when the template has no detectable headings. */
  cocAnchor?: TemplateOutlineAnchor | null;
  /** Auto-discovered QtA insertion anchor — last section heading before
   *  the signature / closing-phrase block. `null` when the template has
   *  no detectable headings. */
  qtaAnchor?: TemplateOutlineAnchor | null;
};

export const useTemplateOutline = ({
  case_number,
  enabled = true,
}: {
  case_number: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["template-outline", case_number],
    queryFn: async (): Promise<TemplateOutline | null> => {
      const url = `/api/v1/cases/get-case-by-case-number/${encodeURIComponent(
        case_number
      )}/template-outline`;
      const result = await apiClient.get<TemplateOutline>({ url });
      if (result.error) {
        // 404 == case has no uploaded template; the editor falls back
        // to local heuristics in that case, so don't blow up the query.
        const status = (result.response as Response | undefined)?.status;
        if (status === 404) return null;
        throw new Error(
          `Failed to load template outline: ${JSON.stringify(result.error)}`
        );
      }
      return (result.data as TemplateOutline | undefined) ?? null;
    },
    enabled: enabled && !!case_number,
    // MDRS-509: the editor's auto-insert effect now waits on this query, so
    // the default three exponential-backoff retries would stall the preamble,
    // QtA and documents-table inserts for ~7s before the fallbacks fire. One
    // retry keeps a transient blip recoverable without holding the editor.
    retry: 1,
    // Outline only changes when the user re-uploads the template;
    // backend cache-invalidates automatically via sourcePath. Cache
    // aggressively here too so we don't refetch on every editor remount.
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 1 day
  });
};
